// ═══════════════════════════════════════════════════════════════════════════
// GESTION COMPTOIR - Interface de gestion des ventes directes et pertes
// ═══════════════════════════════════════════════════════════════════════════
// frontend/src/modules/admin/components/GestionComptoir.jsx
// VERSION 2 : Avec Import/Export Excel
// Farm-Booking - Papy 73+
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { 
  Store, 
  Package, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  Check,
  Edit2,
  Save,
  X,
  Leaf,
  Euro,
  Trash2,
  RefreshCw,
  Calendar,
  BarChart3,
  Download,
  Upload,
  FileSpreadsheet
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionComptoir = () => {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════
  const [periodes, setPeriodes] = useState([]);
  const [selectedPeriode, setSelectedPeriode] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  
  const fileInputRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT INITIAL
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    loadPeriodes();
  }, []);

  useEffect(() => {
    if (selectedPeriode) {
      loadStocks(selectedPeriode);
      loadStats(selectedPeriode);
    }
  }, [selectedPeriode]);

  // ═══════════════════════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════════════════════
  const loadPeriodes = async () => {
    try {
      const res = await fetch(`${API_BASE}/comptoir/periodes-actives`);
      const data = await res.json();
      if (data.success) {
        setPeriodes(data.data);
        if (data.data.length > 0 && !selectedPeriode) {
          setSelectedPeriode(data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement périodes:', error);
      showMessage('Erreur chargement périodes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStocks = async (periodeId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/comptoir/stock/${periodeId}`);
      const data = await res.json();
      if (data.success) {
        setStocks(data.data);
        setTotaux(data.totaux);
      }
    } catch (error) {
      console.error('Erreur chargement stocks:', error);
      showMessage('Erreur chargement stocks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (periodeId) => {
    try {
      const res = await fetch(`${API_BASE}/comptoir/stats/${periodeId}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT / IMPORT EXCEL
  // ═══════════════════════════════════════════════════════════════════════════
  const handleExport = () => {
    if (!selectedPeriode) return;
    window.location.href = `${API_BASE}/comptoir/export/${selectedPeriode}`;
    showMessage('📥 Téléchargement Excel en cours...', 'success');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedPeriode) return;

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_BASE}/comptoir/import/${selectedPeriode}`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        let msg = `✅ ${data.data.updated} produits mis à jour`;
        if (data.data.alertes > 0) {
          msg += ` ⚠️ ${data.data.alertes} avec pertes`;
        }
        showMessage(msg, data.data.alertes > 0 ? 'warning' : 'success');
        loadStocks(selectedPeriode);
        loadStats(selectedPeriode);
      } else {
        showMessage(`❌ ${data.message}`, 'error');
      }
    } catch (error) {
      showMessage('Erreur import fichier', 'error');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════
  const showMessage = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleEdit = (stock) => {
    setEditingId(stock.id);
    setEditValues({
      qte_vendue_comptoir: stock.qte_vendue_comptoir || 0,
      qte_perte: stock.qte_perte || 0,
      notes_comptoir: stock.notes_comptoir || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSaveEdit = async (stockId) => {
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/comptoir/saisie/${stockId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues)
      });
      
      const data = await res.json();
      
      if (data.success) {
        showMessage(data.message, 'success');
        setEditingId(null);
        setEditValues({});
        loadStocks(selectedPeriode);
        loadStats(selectedPeriode);
      } else {
        showMessage(data.message, 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      showMessage('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Calcul du reste prévisionnel pendant l'édition
  const getRestePreview = (stock) => {
    if (editingId !== stock.id) return null;
    const dispo = parseFloat(stock.qte_dispo_comptoir || 0);
    const vendu = parseFloat(editValues.qte_vendue_comptoir || 0);
    const perte = parseFloat(editValues.qte_perte || 0);
    return Math.max(0, dispo - vendu - perte);
  };

  // Validation saisie
  const isValidEdit = (stock) => {
    if (editingId !== stock.id) return true;
    const dispo = parseFloat(stock.qte_dispo_comptoir || 0);
    const vendu = parseFloat(editValues.qte_vendue_comptoir || 0);
    const perte = parseFloat(editValues.qte_perte || 0);
    return (vendu + perte) <= dispo;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <div className="space-y-6">
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Store className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🏪 Gestion Comptoir</h1>
            <p className="text-sm text-gray-500">Ventes directes & pertes</p>
          </div>
        </div>

        {/* Sélecteur période + Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="h-5 w-5 text-gray-400" />
          <select
            value={selectedPeriode || ''}
            onChange={(e) => setSelectedPeriode(parseInt(e.target.value))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            {periodes.map(p => (
              <option key={p.id} value={p.id}>
                {p.nom} - {formatDate(p.date_livraison)}
              </option>
            ))}
          </select>
          
          {/* ═══════════════════════════════════════════════════════════════════
              BOUTONS EXPORT / IMPORT
          ═══════════════════════════════════════════════════════════════════ */}
          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={handleExport}
              disabled={!selectedPeriode || stocks.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Exporter Excel"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            
            <button
              onClick={handleImportClick}
              disabled={!selectedPeriode || importing}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Importer Excel"
            >
              {importing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Import</span>
            </button>
            
            {/* Input fichier caché */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              className="hidden"
            />
          </div>
          
          <button
            onClick={() => loadStocks(selectedPeriode)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Rafraîchir"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          MESSAGE
      ═══════════════════════════════════════════════════════════════════════ */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-800' :
          message.type === 'error' ? 'bg-red-100 text-red-800' :
          message.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {message.type === 'success' ? <Check className="h-5 w-5" /> :
           message.type === 'error' ? <AlertTriangle className="h-5 w-5" /> :
           message.type === 'warning' ? <AlertTriangle className="h-5 w-5" /> :
           <Store className="h-5 w-5" />}
          {message.text}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          STATS CARDS
      ═══════════════════════════════════════════════════════════════════════ */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* CA Comptoir */}
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Euro className="h-8 w-8 opacity-80" />
              <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                Comptoir
              </span>
            </div>
            <div className="text-2xl font-bold">
              {parseFloat(stats.ca_comptoir || 0).toFixed(2)}€
            </div>
            <div className="text-amber-100 text-sm">CA ventes directes</div>
          </div>

          {/* Quantité vendue */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 opacity-80" />
              <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                {stats.taux_vente_comptoir || 0}%
              </span>
            </div>
            <div className="text-2xl font-bold">
              {parseFloat(stats.total_vendu_comptoir || 0).toFixed(1)}
            </div>
            <div className="text-green-100 text-sm">Unités vendues</div>
          </div>

          {/* Pertes */}
          <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Trash2 className="h-8 w-8 opacity-80" />
              <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                {stats.taux_perte || 0}%
              </span>
            </div>
            <div className="text-2xl font-bold">
              {parseFloat(stats.total_pertes || 0).toFixed(1)}
            </div>
            <div className="text-red-100 text-sm">
              Pertes ({parseFloat(stats.valeur_pertes || 0).toFixed(2)}€)
            </div>
          </div>

          {/* Reste */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 opacity-80" />
              <span className="text-xs font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full">
                Stock
              </span>
            </div>
            <div className="text-2xl font-bold">
              {parseFloat(stats.total_reste || 0).toFixed(1)}
            </div>
            <div className="text-blue-100 text-sm">Reste en stock</div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TABLEAU STOCKS COMPTOIR
      ═══════════════════════════════════════════════════════════════════════ */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-amber-500" />
          Chargement...
        </div>
      ) : stocks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Store className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">Aucun produit récolté pour cette période</p>
          <p className="text-sm text-gray-400">
            Les produits apparaissent ici après la saisie de la récolte
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Produit
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Récolté
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-blue-600 uppercase">
                    Résa
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-amber-600 uppercase font-bold">
                    Dispo Comptoir
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-green-600 uppercase">
                    Vendu
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-red-600 uppercase">
                    Pertes
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-purple-600 uppercase">
                    Reste
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stocks.map((stock) => (
                  <tr 
                    key={stock.id} 
                    className={`hover:bg-gray-50 ${
                      editingId === stock.id ? 'bg-amber-50' : ''
                    } ${
                      !isValidEdit(stock) ? 'bg-red-50' : ''
                    }`}
                  >
                    {/* Produit */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {stock.produit_image ? (
                          <img 
                            src={stock.produit_image} 
                            alt="" 
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Leaf className="h-5 w-5 text-amber-500" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{stock.produit_nom}</div>
                          <div className="text-xs text-gray-500">
                            {stock.produit_unite} • {stock.prix_unitaire}€
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Récolté */}
                    <td className="px-3 py-3 text-center text-gray-700">
                      {stock.qte_recoltee}
                    </td>

                    {/* Réservé */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-blue-600 font-medium">{stock.qte_reservee}</span>
                    </td>

                    {/* Dispo Comptoir */}
                    <td className="px-3 py-3 text-center">
                      <span className="text-amber-600 font-bold text-lg">
                        {stock.qte_dispo_comptoir}
                      </span>
                    </td>

                    {/* Vendu */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={editValues.qte_vendue_comptoir}
                          onChange={(e) => setEditValues({
                            ...editValues, 
                            qte_vendue_comptoir: e.target.value
                          })}
                          className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                            !isValidEdit(stock) ? 'border-red-500 bg-red-50' : 'border-green-300'
                          }`}
                        />
                      ) : (
                        <span className={`font-medium ${
                          parseFloat(stock.qte_vendue_comptoir) > 0 
                            ? 'text-green-600' 
                            : 'text-gray-400'
                        }`}>
                          {stock.qte_vendue_comptoir || 0}
                        </span>
                      )}
                    </td>

                    {/* Pertes */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={editValues.qte_perte}
                          onChange={(e) => setEditValues({
                            ...editValues, 
                            qte_perte: e.target.value
                          })}
                          className={`w-20 px-2 py-1 border rounded text-center text-sm ${
                            !isValidEdit(stock) ? 'border-red-500 bg-red-50' : 'border-red-300'
                          }`}
                        />
                      ) : (
                        <span className={`font-medium ${
                          parseFloat(stock.qte_perte) > 0 
                            ? 'text-red-600' 
                            : 'text-gray-400'
                        }`}>
                          {stock.qte_perte || 0}
                        </span>
                      )}
                    </td>

                    {/* Reste */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <span className={`font-bold ${
                          !isValidEdit(stock) ? 'text-red-600' : 'text-purple-600'
                        }`}>
                          {getRestePreview(stock)?.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-purple-600 font-medium">
                          {stock.qte_reste || 0}
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3 text-center">
                      {editingId === stock.id ? (
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => handleSaveEdit(stock.id)} 
                            disabled={!isValidEdit(stock) || saving}
                            className={`p-1.5 rounded transition-colors ${
                              isValidEdit(stock) && !saving
                                ? 'text-green-600 hover:bg-green-100' 
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title="Enregistrer"
                          >
                            <Save className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={handleCancelEdit} 
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="Annuler"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={() => handleEdit(stock)} 
                          className="p-1.5 text-amber-600 hover:bg-amber-100 rounded transition-colors"
                          title="Saisir ventes/pertes"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {/* LIGNE TOTAUX */}
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-4 py-3 text-gray-700">
                    TOTAUX
                  </td>
                  <td className="px-3 py-3 text-center text-gray-700">
                    {totaux.total_recolte?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-blue-600">
                    {totaux.total_reserve?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-amber-600">
                    {totaux.total_dispo_comptoir?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-green-600">
                    {totaux.total_vendu?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-red-600">
                    {totaux.total_pertes?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-purple-600">
                    {totaux.total_reste?.toFixed(1)}
                  </td>
                  <td className="px-3 py-3 text-center text-gray-500">
                    <BarChart3 className="h-5 w-5 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer avec CA */}
          <div className="bg-amber-50 px-4 py-3 border-t flex justify-between items-center">
            <div className="text-sm text-amber-700">
              💰 CA Comptoir : <span className="font-bold text-lg">{totaux.ca_comptoir?.toFixed(2)}€</span>
            </div>
            <div className="text-sm text-red-600">
              ⚠️ Valeur pertes : <span className="font-medium">{totaux.valeur_pertes?.toFixed(2)}€</span>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          AIDE
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
        <h3 className="font-semibold text-amber-800 mb-2">💡 Mode d'emploi</h3>
        <ul className="text-sm text-amber-700 space-y-1">
          <li>• <strong>Dispo Comptoir</strong> = Récolté - Réservé (ce qui reste pour la vente directe)</li>
          <li>• Cliquez sur ✏️ pour saisir les ventes et pertes de la journée</li>
          <li>• Le <strong>Reste</strong> se calcule automatiquement (sera reporté à la période suivante)</li>
          <li>• <FileSpreadsheet className="h-4 w-4 inline text-green-600" /> <strong>Export</strong> : télécharger un fichier Excel pré-rempli</li>
          <li>• <FileSpreadsheet className="h-4 w-4 inline text-blue-600" /> <strong>Import</strong> : importer les ventes/pertes depuis Excel</li>
        </ul>
      </div>
    </div>
  );
};

export default GestionComptoir;
